/**
 * gameCollectionAdapter — CollectionAdapter for the videogame-tracker app.
 * Configures UnifiedCollectionView with game data, filters, sorts, and renderers.
 * Supports multi-user view modes via projectUserFields / _userData.
 *
 * Users are loaded dynamically from the UsersProvider context.
 */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Card, StarRating, InlineEditCell, formatDate, Pill } from '@code-burrow/design-system';
import { UnifiedCollectionView, useUserView, useUsers, usePod, useCurrentUser, projectUserFields, mergeUserData, filterPodItems, filterToUserItems, isCombinedView, ActivityFeed, ActivityService, CatalogCache, InlineActivityCell } from '@code-burrow/tracker-core';
import { GameStatus } from '../models/GameEntry.js';
import GameDetailModal from '../components/GameDetailModal.jsx';

const STATUS_COLORS = {
    [GameStatus.COMPLETED]: 'bg-emerald-500/15 text-emerald-300',
    [GameStatus.PLAYING]: 'bg-blue-500/15 text-blue-400',
    [GameStatus.WANT_TO_PLAY]: 'bg-violet-500/15 text-violet-400',
    [GameStatus.NOT_STARTED]: 'bg-amber-500/15 text-amber-400',
    [GameStatus.DIDNT_FINISH]: 'bg-orange-500/15 text-orange-400',
    [GameStatus.ABANDONED]: 'bg-zinc-500/15 text-zinc-400',
};

/** Format playtime minutes into a human-readable string */
function formatPlayTime(minutes) {
    if (!minutes || minutes <= 0) return null;
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours >= 100) return `${hours}h`; // skip minutes for triple-digit hours
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function StatusPill({ status }) {
    if (!status) return null;
    return <Pill className={STATUS_COLORS[status] || 'bg-zinc-500/15 text-zinc-400'}>{status}</Pill>;
}

/** Ownership platform definitions */
const OWNERSHIP_PLATFORMS = [
    { key: 'steam', label: 'Steam' },
    { key: 'xbox', label: 'Xbox' },
    { key: 'switch', label: 'Nintendo Switch' },
    { key: 'playstation', label: 'PlayStation' },
];

/** Platform icon badge for card overlay and detail modal */
function PlatformBadge({ source, className = '' }) {
    const src = source || 'steam';
    if (src === 'xbox') {
        return (
            <div className={`flex items-center justify-center ${className}`} title="Xbox">
                <img src="/xbox-logo.svg" alt="Xbox" className="w-full h-full object-contain" />
            </div>
        );
    }
    if (src === 'switch') {
        return (
            <div className={`flex items-center justify-center ${className}`} title="Nintendo Switch">
                <svg className="w-full h-full" viewBox="0 0 24 24" fill="#E60012">
                    <path d="M7 2C4.8 2 3 3.8 3 6v12c0 2.2 1.8 4 4 4h3V2H7zm0 15.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3z"/>
                    <path d="M17 2h-3v20h3c2.2 0 4-1.8 4-4V6c0-2.2-1.8-4-4-4zm0 6.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" fill="#0AB9E6"/>
                </svg>
            </div>
        );
    }
    if (src === 'playstation') {
        return (
            <div className={`flex items-center justify-center ${className}`} title="PlayStation">
                <svg className="w-full h-full" viewBox="0 0 24 24" fill="#006FCD">
                    <path d="M9.5 2v16.2l3.5 1.2V6.3c0-.6.3-1.1.8-1.2.6-.2 .9.2.9.8v12.4l2.5-.9V5.7c0-1.5-.6-2.7-2-3.2C13.4 2 11.4 2 9.5 2zM4.8 17.1c-1.4.5-1.6 1.5-.6 2.3.9.7 2.6 1.1 4.1.7V18l-2-.7c-.6-.2-.7-.5-.2-.7.4-.2 1.2-.1 2.2.3v-2.1c-1.4-.3-2.8-.2-3.5.3zm14.7-.4c-1-.4-2.3-.5-3.3-.2l-.5.2v2.1l2.4-.8c.6-.2.7.1.2.3l-2.6 1v2.2l.5-.2 3.6-1.5c.7-.3 .7-.8 .2-1.2-.5-.4-1.4-.7-2.5-.9z" transform="translate(0, -1)"/>
                </svg>
            </div>
        );
    }
    // Steam default
    return (
        <div className={`flex items-center justify-center ${className}`} title="Steam">
            <svg className="w-full h-full" viewBox="0 0 24 24" fill="#66C0F4">
                <path d="M12 2C6.5 2 2 6.3 2 11.6c0 .5 0 .9.1 1.4l5.2 2.1c.4-.3 1-.5 1.6-.5h.1l2.3-3.3v-.1c0-2 1.6-3.6 3.6-3.6s3.6 1.6 3.6 3.6-1.6 3.6-3.6 3.6h-.1l-3.2 2.4c0 .5-.1 1-.3 1.4l-3 .5c1 2 3.1 3.4 5.6 3.4C17.5 22 22 17.5 22 12S17.5 2 12 2z"/>
                <circle cx="15" cy="11.2" r="2.2"/>
                <path d="M7.5 16.8l-1.4-.6c.2.6.7 1 1.3 1.1.8.2 1.6-.3 1.8-1.1.1-.4.1-.8-.1-1.1-.2-.4-.5-.6-.9-.7l1.4.6c.6.3.9 1 .7 1.7-.3.7-1 1-1.7.7-.4-.2-.8-.4-1.1-.6z"/>
            </svg>
        </div>
    );
}

export { PlatformBadge, OWNERSHIP_PLATFORMS };

const GAME_USER_FIELDS = ['status', 'rating', 'lastPlayed', 'completedDate', 'playTime', 'progress', 'earnedAchievements', 'totalAchievements', 'ownership'];

/**
 * GameCollectionView — wraps UnifiedCollectionView with a detail modal.
 * Reads activeView from UserViewContext and users from UsersProvider.
 */
export function GameCollectionView({ backendService, onItemsLoaded, externalSelect, pageMode = 'library', onSearchOpen }) {
    const [selectedGame, setSelectedGame] = useState(externalSelect || null);
    const [refreshKey, setRefreshKey] = useState(0);
    const { activeView } = useUserView();
    const { users } = useUsers();
    const { pod, allowedUsers, pods, members, allMembers } = usePod();
    const currentUser = useCurrentUser();
    const itemActionsRef = useRef(null);

    // All pod members' avatars are always shown on cards
    const visibleUserIds = useMemo(() => {
        const ids = new Set();
        for (const p of (pods || [])) {
            for (const m of (p.members || [])) {
                if (m.userId !== currentUser?.id) ids.add(m.userId);
            }
        }
        return ids;
    }, [pods, currentUser?.id]);

    React.useEffect(() => {
        if (externalSelect) setSelectedGame(externalSelect);
    }, [externalSelect]);

    const handleRefresh = useCallback(() => {
        setRefreshKey((k) => k + 1);
    }, []);

    const handleItemActions = useCallback((actions) => {
        itemActionsRef.current = actions;
    }, []);

    // Update the saved game's card in place so the grid reflects the edit the moment
    // the modal closes — no full refetch needed. Re-project so top-level fields
    // (status, rating, lastPlayed, …) update alongside _userData.
    // A just-added game isn't in the loaded list yet, so there is nothing to patch:
    // drop the cached catalog and refetch so it appears.
    const handleSaved = useCallback(async (updatedGame) => {
        if (updatedGame?.id && itemActionsRef.current) {
            const projected = projectUserFields(updatedGame, 'videogames', activeView, users);
            if (itemActionsRef.current.replaceItem(updatedGame.id, projected)) return;
        }
        await backendService.clearCatalogCache();
        setRefreshKey((k) => k + 1);
    }, [activeView, users, backendService]);

    const allowedUserIds = useMemo(() => allowedUsers('videogames'), [allowedUsers]);
    const podWithMembers = useMemo(() => pod ? { ...pod, members } : null, [pod, members]);

    // Activity service for co-participant data + feed
    const activityServiceRef = useRef(null);
    if (!activityServiceRef.current && currentUser?.id) {
        activityServiceRef.current = new ActivityService(currentUser.id);
    }

    const adapter = useMemo(() =>
        gameCollectionAdapter(backendService, setSelectedGame, refreshKey, activeView, pageMode, users, allowedUserIds, podWithMembers, onSearchOpen, currentUser, activityServiceRef.current, visibleUserIds, allMembers, handleRefresh),
        [backendService, refreshKey, activeView, pageMode, users, allowedUserIds, podWithMembers, onSearchOpen, currentUser, visibleUserIds, allMembers, handleRefresh]
    );
    const feedAdapter = useMemo(() =>
        isCombinedView(activeView) && activityServiceRef.current && podWithMembers ? {
            ...adapter,
            feedRenderer: () => (
                <ActivityFeed
                    activityService={activityServiceRef.current}
                    podId={podWithMembers.id}
                    users={users}
                    onItemClick={(activity) => {
                        if (activity.itemType === 'game') {
                            setSelectedGame({ id: activity.itemId, steamID: activity.itemId, title: activity.itemTitle });
                        }
                    }}
                />
            ),
        } : adapter,
        [adapter, activeView, podWithMembers, users]
    );

    return (
        <>
            <UnifiedCollectionView collectionAdapter={feedAdapter} onItemsLoaded={onItemsLoaded} refreshKey={`${refreshKey}-${activeView}-${pageMode}-${users.length}`} onItemActions={handleItemActions} />
            {selectedGame && (
                <GameDetailModal
                    game={selectedGame}
                    backendService={backendService}
                    onClose={() => setSelectedGame(null)}
                    onSaved={handleSaved}
                    onDeleted={handleRefresh}
                />
            )}
        </>
    );
}

const STATUS_OPTIONS = Object.values(GameStatus).map((v) => ({ value: v, label: v, pillClassName: STATUS_COLORS[v] }));

/**
 * Helper: get user data field from an item.
 * Gets a user data field from an item's _userData map.
 */
function getUserField(item, userName, fieldName) {
    if (item._userData?.[userName]) {
        return item._userData[userName][fieldName] ?? null;
    }
    return null;
}

export function gameCollectionAdapter(backendService, onItemClick = null, _refreshKey = 0, activeView = 'benny', pageMode = 'library', users = [], allowedUserIds = null, pod = null, onSearchOpen = null, currentUser = null, activityService = null, visibleUserIds = null, allMembers = [], onRefresh = null) {
    const currentYear = new Date().getFullYear();
    const isSingleUser = !isCombinedView(activeView);
    const user = isSingleUser ? activeView : null;

    /**
     * Check if a game has a real status (not empty, not "Not played") for the relevant user(s).
     */
    const hasRealStatus = (rawItem) => {
        const check = (userName) => {
            const s = getUserField(rawItem, userName, 'status');
            return s && s !== '' && s !== GameStatus.NOT_STARTED;
        };
        if (user) return check(user);
        const userNames = users.length > 0 ? users.map((u) => u.name) : ['benny', 'gareth'];
        return userNames.some(check);
    };

    /**
     * Check if a game belongs in the given year for the relevant user(s).
     * Uses completedDate for completed games, lastPlayed otherwise.
     */
    const playedInYear = (rawItem, year) => {
        const check = (userName) => {
            const status = getUserField(rawItem, userName, 'status');
            const completed = getUserField(rawItem, userName, 'completedDate');
            // Completed games belong to their completion year
            if (status === GameStatus.COMPLETED && completed) {
                return new Date(completed).getFullYear() === year;
            }
            // Other games use lastPlayed
            const d = getUserField(rawItem, userName, 'lastPlayed');
            return d && new Date(d).getFullYear() === year;
        };
        if (user) return check(user);
        const userNames = users.length > 0 ? users.map((u) => u.name) : ['benny', 'gareth'];
        return userNames.some(check);
    };

    // Storage key varies by page so filters/sort persist independently
    const storageKeyMap = {
        thisYear: 'game-tracker',
        library: 'game-tracker-library',
    };

    const processGameData = (collectionData, coParticipantMap, hasCoParticipantData = true) => {
        let rawItems = collectionData.items;
        for (const item of rawItems) {
            item._coParticipantIds = coParticipantMap[item.id] || coParticipantMap[item.steamID] || [];
        }
        if (pageMode === 'pods') {
            rawItems = filterPodItems(rawItems, 'videogames', [GameStatus.NOT_STARTED, GameStatus.WANT_TO_PLAY], users, 1);
        } else {
            rawItems = filterToUserItems(rawItems, activeView);
        }
        let items = rawItems.map((item) => projectUserFields(item, 'videogames', activeView, users));

        // ── Pods page: reconcile "together" data with explicit activities ──────
        // PodsPage's "Played Together" uses collection overlap on status (d3 checks
        // status, excluding NOT_STARTED/WANT_TO_PLAY); the activity feed uses lastPlayed.
        // Both need patching so _coParticipantIds is the authoritative "together" signal.
        const EXCLUDED_STATUSES = new Set([GameStatus.NOT_STARTED, GameStatus.WANT_TO_PLAY]);
        if (pageMode === 'pods' && currentUser?.name && pod?.members?.length > 0) {
            const podMemberByUserId = Object.fromEntries(pod.members.map((m) => [m.userId, m]));
            const dismissedKey = `pods-dismissed-${currentUser.id}`;
            const dismissed = (() => {
                try { return new Set(JSON.parse(localStorage.getItem(dismissedKey) || '[]')); }
                catch { return new Set(); }
            })();
            for (let idx = 0; idx < items.length; idx++) {
                const item = items[idx];
                const coParticipantIds = new Set(item._coParticipantIds || []);
                const currentUserData = item._userData?.[currentUser.name];
                const currentUserEngaged = currentUserData?.status && !EXCLUDED_STATUSES.has(currentUserData.status);
                let newUserData = null;
                let isSyntheticForCurrentUser = false;

                // Part 1: Ensure all explicitly-together participants have an engaged status.
                // A single user marking something "together" is sufficient — synthesize for
                // anyone who hasn't independently added the item yet (including the current user).
                if (coParticipantIds.size > 0) {
                    let referenceStatus = currentUserEngaged ? currentUserData.status : null;
                    let referenceLastPlayed = currentUserData?.lastPlayed || null;
                    if (!referenceStatus) {
                        for (const memberId of coParticipantIds) {
                            const m = podMemberByUserId[memberId];
                            if (m) {
                                const d = item._userData?.[m.userName];
                                if (d?.status && !EXCLUDED_STATUSES.has(d.status)) {
                                    referenceStatus = d.status;
                                    referenceLastPlayed = referenceLastPlayed || d.lastPlayed || null;
                                    break;
                                }
                            }
                        }
                    }
                    if (referenceStatus) {
                        if (!currentUserEngaged && !dismissed.has(item.id)) {
                            if (!newUserData) newUserData = { ...item._userData };
                            newUserData[currentUser.name] = {
                                ...(item._userData?.[currentUser.name] || {}),
                                status: referenceStatus,
                                lastPlayed: referenceLastPlayed || currentUserData?.lastPlayed || null,
                            };
                            isSyntheticForCurrentUser = true;
                        }
                        for (const memberId of coParticipantIds) {
                            const m = podMemberByUserId[memberId];
                            if (!m) continue;
                            const mData = (newUserData || item._userData)?.[m.userName];
                            const mEngaged = mData?.status && !EXCLUDED_STATUSES.has(mData.status);
                            if (!mEngaged) {
                                if (!newUserData) newUserData = { ...item._userData };
                                newUserData[m.userName] = {
                                    ...(item._userData?.[m.userName] || {}),
                                    status: referenceStatus,
                                    lastPlayed: referenceLastPlayed || mData?.lastPlayed || null,
                                };
                            }
                        }
                    }
                }

                // Part 2: Clear status + lastPlayed for non-co-participants who have them
                // → prevents false "together" in both "Played Together" and the activity feed.
                // Skip when coMaps are empty (cached load) — would incorrectly strip all data.
                const effectiveCurrentData = (newUserData || item._userData)?.[currentUser.name];
                const effectiveCurrentEngaged = effectiveCurrentData?.status && !EXCLUDED_STATUSES.has(effectiveCurrentData.status);
                if (hasCoParticipantData && effectiveCurrentEngaged) {
                    for (const [memberId, member] of Object.entries(podMemberByUserId)) {
                        if (memberId === currentUser.id) continue;
                        if (coParticipantIds.has(memberId)) continue;
                        const mData = (newUserData || item._userData)?.[member.userName];
                        const mEngaged = mData?.status && !EXCLUDED_STATUSES.has(mData.status);
                        if (mEngaged) {
                            if (!newUserData) newUserData = { ...item._userData };
                            newUserData[member.userName] = { ...mData, status: null, lastPlayed: null };
                        }
                    }
                }

                if (newUserData) {
                    items[idx] = { ...item, _userData: newUserData, ...(isSyntheticForCurrentUser && { _syntheticForCurrentUser: true }) };
                }
            }
        }

        if (activeView === 'pod' && pageMode !== 'pods') {
            const podIds = new Set(
                filterPodItems(rawItems, 'videogames', [GameStatus.NOT_STARTED, GameStatus.WANT_TO_PLAY], users, pod?.togetherThreshold).map((i) => i.steamID)
            );
            items = items.filter((i) => podIds.has(i.steamID));
        }
        if (pageMode === 'thisYear') {
            const activeIds = new Set(rawItems.filter((r) => hasRealStatus(r)).map((r) => r.steamID));
            items = items.filter((i) => activeIds.has(i.steamID));
        }
        return items;
    };

    return {
        storageKey: storageKeyMap[pageMode] || 'game-tracker',
        defaultSortField: 'date',
        defaultSortDirection: 'desc',
        getTabValue: pageMode === 'thisYear'
            ? (item) => {
                const d = item.completedDate || item.lastPlayed;
                return d ? new Date(d).getFullYear() : null;
            }
            : null,

        // ── Data fetching ──────────────────────────────────────────────────────
        fetchItems: async () => {
            const catalogCache = new CatalogCache('videogames');
            const fullCache = new CatalogCache(`videogames:${currentUser?.id}`);

            const getCatalog = async () => {
                const c = await catalogCache.get();
                if (c?.items) return c.items;
                const data = await backendService.getCatalog();
                catalogCache.set(data.items).catch(() => {});
                return data.items;
            };

            const [catalogItems, userData, coParticipantMap] = await Promise.all([
                getCatalog(),
                backendService.getUserData(currentUser?.id),
                activityService ? activityService.getCoParticipantMap('game') : Promise.resolve({}),
            ]);

            let items = [...catalogItems];
            const allowedIds = new Set((userData.users || []).map((u) => u.id));
            for (const [, jt] of Object.entries(userData.joinData || {})) {
                items = mergeUserData(items, jt.records, jt.itemIdField, jt.userFields, userData.users, allowedIds);
            }
            const collectionData = { items, users: userData.users };
            fullCache.set(collectionData).catch(() => {});
            return processGameData(collectionData, coParticipantMap);
        },

        getCachedItems: async () => {
            const fullCache = new CatalogCache(`videogames:${currentUser?.id}`);
            const cached = await fullCache.getEvenIfStale();
            if (!cached?.items) return null;
            try {
                const items = processGameData(cached.items, {}, false);
                return { items, isStale: cached.isStale, timestamp: cached.timestamp };
            } catch { return null; }
        },

        // ── Context menu ──────────────────────────────────────────────────────
        backendService,
        activeView,
        getContextMenuActions: (item) => [
            {
                label: 'Refresh Metadata',
                onClick: async () => {
                    const result = await backendService.refreshGame(item.steamID);
                    if (result && !result.success) throw new Error(result.error);
                },
            },
            {
                label: 'Delete',
                variant: 'danger',
                onClick: async () => {
                    try {
                        const joins = await backendService.pocketbase.collection('user_games').getFullList({
                            filter: `game="${item.id}"`, $autoCancel: false,
                        });
                        for (const j of joins) await backendService.proxy.collection('user_games').delete(j.id);
                    } catch {}
                    await backendService.deleteGame(item.steamID);
                },
            },
        ],

        // ── Filter fields ──────────────────────────────────────────────────────
        filterFields: [
            {
                key: 'status',
                label: 'Status',
                type: 'multiselect',
                allLabel: 'All Statuses',
                targetFields: ['status'],
                getOptions: (items) =>
                    [...new Set(items.map((i) => i.status).filter(Boolean))].sort(),
            },
        ],

        // ── Sort fields ────────────────────────────────────────────────────────
        sortFields: [
            {
                key: 'title',
                label: 'Title',
                getValue: (item) => (item.title ?? '').toLowerCase(),
            },
            {
                key: 'creator',
                label: 'Creator',
                getValue: (item) => (item.creator ?? '').toLowerCase(),
            },
            {
                key: 'status',
                label: 'Status',
                getValue: (item) => item.status ?? '',
            },
            {
                key: 'rating',
                label: 'Rating',
                getValue: (item) => item.rating ?? null,
            },
            {
                key: 'date',
                label: 'Date',
                getValue: (item) => {
                    const d = item.completedDate || item.lastPlayed;
                    return d ? new Date(d).getTime() : null;
                },
            },
            {
                key: 'playTime',
                label: 'Play Time',
                getValue: (item) => item.playTime ?? null,
            },
            {
                key: 'progress',
                label: 'Progress',
                getValue: (item) => item.progress ?? null,
            },
            {
                key: 'source',
                label: 'Source',
                getValue: (item) => item.source ?? 'steam',
            },
            {
                key: 'releaseDate',
                label: 'Release Date',
                getValue: (item) =>
                    item.releaseDate ? new Date(item.releaseDate).getTime() : null,
            },
        ],

        // ── Grid item renderer ─────────────────────────────────────────────────
        renderGridItem: (item) => {
            const year = item.releaseDate
                ? new Date(item.releaseDate).getFullYear()
                : null;

            // Combined view (all/pod): show per-user status pills
            let statusWithDate;
            if (isCombinedView(activeView)) {
                // Build per-user statuses dynamically
                const userStatuses = (users.length > 0 ? users : []).map((u) => ({
                    name: u.name,
                    emoji: u.emoji,
                    status: getUserField(item, u.name, 'status') || null,
                })).filter((us) => us.status);

                const allSame = userStatuses.length > 1 &&
                    userStatuses.every((us) => us.status === userStatuses[0].status);

                statusWithDate = (userStatuses.length > 0 || item.lastPlayed) ? (
                    <div className="space-y-1">
                        {allSame && userStatuses.length > 0 ? (
                            <div className="flex items-center gap-0.5">
                                <StatusPill status={userStatuses[0].status} />
                            </div>
                        ) : (
                            <>
                                {userStatuses.map((us) => (
                                    <div key={us.name} className="flex items-center gap-1.5">
                                        <span className="text-[10px] w-3">{us.emoji}</span>
                                        <StatusPill status={us.status} />
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                ) : null;
            } else {
                statusWithDate = (item.status || item.lastPlayed) ? (
                    <div className="flex items-center gap-1">
                        <StatusPill status={item.status} />
                    </div>
                ) : null;
            }

            // Source-specific stat: play time for Steam, progress for Xbox
            const isXbox = (item.source || 'steam') === 'xbox';
            let statDisplay;
            if (isXbox) {
                const pct = item.progress;
                if (pct != null) {
                    const color = pct === 100 ? 'text-emerald-400' : pct > 0 ? 'text-blue-400' : 'text-text-tertiary';
                    statDisplay = (
                        <div className="mt-1.5 flex items-center gap-1.5">
                            <div className="flex-1 h-1.5 bg-bg-tertiary rounded-full overflow-hidden min-w-[40px]">
                                <div className={`h-full rounded-full ${pct === 100 ? 'bg-emerald-400' : 'bg-blue-400'}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className={`text-[11px] font-medium ${color}`}>{pct}%</span>
                        </div>
                    );
                }
            } else {
                // For combined view, pick max play time across users
                let playTimeVal;
                if (isCombinedView(activeView)) {
                    const times = (users.length > 0 ? users : []).map((u) => getUserField(item, u.name, 'playTime') || 0).filter((t) => t > 0);
                    playTimeVal = times.length > 0 ? Math.max(...times) : null;
                } else {
                    playTimeVal = item.playTime;
                }
                const playTimeStr = formatPlayTime(playTimeVal);
                if (playTimeStr) {
                    statDisplay = (
                        <div className="mt-1.5 flex items-center gap-1 text-[11px] text-text-tertiary">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {playTimeStr}
                        </div>
                    );
                }
            }

            // Date line: show completed date (priority) or last played
            let dateDisplay;
            if (isCombinedView(activeView)) {
                // Prefer completedDate across users, fall back to lastPlayed
                const userNames = users.length > 0 ? users : [];
                const completedDates = userNames.map((u) => getUserField(item, u.name, 'completedDate')).filter(Boolean);
                const lastPlayedDates = userNames.map((u) => getUserField(item, u.name, 'lastPlayed')).filter(Boolean);
                const best = completedDates.length > 0
                    ? completedDates.sort().reverse()[0]
                    : lastPlayedDates.length > 0 ? lastPlayedDates.sort().reverse()[0] : null;
                if (best) {
                    dateDisplay = (
                        <div className="mt-1 text-xs text-text-tertiary">
                            {formatDate(best)}
                        </div>
                    );
                }
            } else {
                const dateVal = item.completedDate || item.lastPlayed;
                if (dateVal) {
                    dateDisplay = (
                        <div className="mt-1 text-xs text-text-tertiary">
                            {formatDate(dateVal)}
                        </div>
                    );
                }
            }

            // Derive owned platforms from user data, fall back to source
            let ownedPlatforms = [];
            if (isCombinedView(activeView)) {
                const allUsers = users.length > 0 ? users : [];
                const seen = new Set();
                for (const u of allUsers) {
                    const ownership = getUserField(item, u.name, 'ownership') || [];
                    for (const p of ownership) seen.add(p);
                }
                ownedPlatforms = [...seen];
            } else {
                const ownership = getUserField(item, activeView, 'ownership') || [];
                ownedPlatforms = [...ownership];
            }

            const platformBadge = ownedPlatforms.length > 0 ? (
                <div className="flex items-center gap-0.5">
                    {ownedPlatforms.map((p) => (
                        <PlatformBadge key={p} source={p} className="w-4 h-4" />
                    ))}
                </div>
            ) : (
                <PlatformBadge source={item.source} className="w-4 h-4" />
            );

            // coParticipants: current user + people who explicitly co-logged WITH them (from activity records only)
            const coPartIds = item._coParticipantIds || [];
            const coPartIdSet = new Set(coPartIds);
            const myAvatar = currentUser ? { emoji: users.find(u => u.name === currentUser.name)?.emoji, name: currentUser.name } : null;

            let coParticipants = undefined;
            if (coPartIds.length > 0 && myAvatar?.emoji) {
                const visibleCoUsers = users.filter(u => coPartIdSet.has(u.id) && (!visibleUserIds || visibleUserIds.has(u.id)));
                if (visibleCoUsers.length > 0) {
                    coParticipants = [
                        myAvatar,
                        ...visibleCoUsers.map(u => ({ emoji: u.emoji, name: u.displayName || u.name })),
                    ];
                }
            }

            return (
                <Card
                    key={item.steamID}
                    imageUrl={item.imageURL ?? null}
                    imageAlt={item.title}
                    title={item.title}
                    year={year}
                    imageBadge={platformBadge}
                    coParticipants={coParticipants}

                    statusPill={statusWithDate}
                    statusMeta={(() => {
                        const syntheticActions = pageMode === 'pods' && item._syntheticForCurrentUser && onRefresh ? (
                            <div className="flex gap-1.5 mt-2">
                                <button
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                            await backendService.upsertUserGame(currentUser.name, item.id, {
                                                status: item._userData?.[currentUser.name]?.status || null,
                                                lastPlayed: item._userData?.[currentUser.name]?.lastPlayed || null,
                                            });
                                            onRefresh();
                                        } catch {}
                                    }}
                                    className="text-xs px-2 py-0.5 rounded bg-accent-primary/20 text-accent-primary hover:bg-accent-primary/30 transition-colors"
                                >
                                    Add to my list
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        try {
                                            const key = `pods-dismissed-${currentUser.id}`;
                                            const set = new Set(JSON.parse(localStorage.getItem(key) || '[]'));
                                            set.add(item.id);
                                            localStorage.setItem(key, JSON.stringify([...set]));
                                        } catch {}
                                        onRefresh();
                                    }}
                                    className="text-xs px-2 py-0.5 rounded bg-surface-secondary text-text-tertiary hover:text-text-primary transition-colors"
                                >
                                    Dismiss
                                </button>
                            </div>
                        ) : null;
                        return (statDisplay || dateDisplay || syntheticActions) ? (
                            <>
                                {statDisplay}
                                {dateDisplay}
                                {syntheticActions}
                            </>
                        ) : null;
                    })()}
                    onClick={onItemClick ? () => onItemClick(item) : undefined}
                />
            );
        },

        // ── Table columns ──────────────────────────────────────────────────────
        renderTableColumns: buildGameTableColumns(backendService, onItemClick, activeView, users, pod, currentUser, activityService, allMembers),

        // ── Bulk edit ────────────────────────────────────────────────────────
        getItemId: (item) => item.steamID,
        bulkEditFields: isCombinedView(activeView) ? [] : [
            { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
            { key: 'rating', label: 'Rating', type: 'rating' },
            { key: 'togetherWith', label: 'Together', type: 'podMembers' },
        ],
        onBulkSave: async (selectedItems, updates) => {
            const errors = [];
            const { togetherWith, ...userUpdates } = updates;
            const hasUserUpdates = Object.keys(userUpdates).length > 0;

            for (let i = 0; i < selectedItems.length; i += 10) {
                const batch = selectedItems.slice(i, i + 10);
                const results = await Promise.allSettled(
                    batch.map(async (item) => {
                        if (hasUserUpdates) {
                            await backendService.upsertUserGame(activeView, item.id, userUpdates);
                        }
                        if (togetherWith && togetherWith.length > 0 && activityService && pod && currentUser) {
                            const memberIds = (pod.members || [])
                                .filter(m => togetherWith.includes(m.userId))
                                .map(m => m.userId);
                            if (memberIds.length > 0) {
                                await activityService.create({
                                    type: 'played',
                                    itemType: 'game',
                                    itemId: item.id,
                                    itemTitle: item.title || '',
                                    participants: [currentUser.id, ...memberIds],
                                    pod: pod.id,
                                    date: new Date().toISOString(),
                                });
                            }
                        }
                    })
                );
                results.forEach((r, idx) => {
                    if (r.status === 'rejected') {
                        errors.push(batch[idx].title);
                    }
                });
            }
            if (errors.length > 0) {
                alert(`Failed to update ${errors.length} item(s): ${errors.slice(0, 5).join(', ')}${errors.length > 5 ? '...' : ''}`);
            }
        },

        // ── Empty state ────────────────────────────────────────────────────────
        emptyState: (() => {
            const isOtherUser = !isCombinedView(activeView) && activeView !== currentUser?.name;
            const viewUser = isOtherUser ? users.find((u) => u.name === activeView) : null;
            const searchBtn = onSearchOpen ? (
                <button onClick={onSearchOpen} className="px-4 py-2 rounded-md bg-accent-primary text-text-inverse text-sm font-medium hover:brightness-90 transition-all">
                    Search & Add
                </button>
            ) : undefined;

            if (isOtherUser) return {
                title: `No games shared by ${viewUser?.displayName || activeView} yet`,
            };
            if (activeView === 'pod') return {
                title: 'No games played together yet',
                description: 'Games your pod has played will appear here',
            };
            if (pageMode === 'thisYear') return {
                title: 'No games played yet',
                description: 'Games you play will show up here',
                action: searchBtn,
            };
            return {
                title: 'No games yet',
                description: !pod
                    ? 'Search to add your first game. You can also create a Pod in Settings to share with friends.'
                    : 'Get started by adding a game to your collection',
                action: searchBtn,
            };
        })(),

        // ── Toolbar actions ────────────────────────────────────────────────────
        actions: [],
    };
}

// ── Table column builder ───────────────────────────────────────────────────────

function buildGameTableColumns(backendService, onItemClick = null, activeView = 'benny', users = [], pod = null, currentUser = null, activityService = null, allMembers = []) {
    const handleSave = async (item, field, value) => {
        const result = await backendService.upsertUserGame(activeView, item.id, { [field]: value });
        if (!result.success) {
            throw new Error(result.error || 'Failed to save');
        }
    };

    const handleMainRecordSave = async (item, field, value) => {
        const result = await backendService.updateGame(item.id, { [field]: value });
        if (!result.success) {
            throw new Error(result.error || 'Failed to save');
        }
    };

    return [
        {
            key: 'image',
            header: '',
            width: '80px',
            renderCell: (item) =>
                item.imageURL ? (
                    <img
                        src={item.imageURL}
                        alt={item.title}
                        className="h-10 w-16 object-cover rounded-sm"
                        loading="lazy"
                    />
                ) : (
                    <div className="h-10 w-16 bg-bg-tertiary rounded-sm flex items-center justify-center">
                        <svg className="h-5 w-5 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.069A1 1 0 0121 8.82v6.36a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
                        </svg>
                    </div>
                ),
        },
        {
            key: 'title',
            header: 'Title',
            sortable: 'title',
            renderCell: (item) =>
                onItemClick ? (
                    <button
                        onClick={() => onItemClick(item)}
                        className="font-medium text-text-primary hover:text-accent-primary transition-colors duration-150 text-left"
                    >
                        {item.title}
                    </button>
                ) : (
                    <span className="font-medium text-text-primary">{item.title}</span>
                ),
        },
        {
            key: 'creator',
            header: 'Creator',
            sortable: 'creator',
            renderCell: (item) => (
                <span className="text-text-secondary">{item.creator ?? '-'}</span>
            ),
        },
        {
            key: 'genres',
            header: 'Genres',
            renderCell: (item) => (
                <span className="text-text-secondary">
                    {item.genres?.slice(0, 3).join(', ') || '-'}
                </span>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            sortable: 'status',
            width: '140px',
            renderCell: (item, { onItemUpdated } = {}) => (
                <InlineEditCell
                    item={item}
                    field="status"
                    editType="select"
                    options={STATUS_OPTIONS}
                    displayValue={item.status ? <StatusPill status={item.status} /> : <span className="text-text-secondary">-</span>}
                    onSave={async (it, field, value) => { await handleSave(it, field, value); onItemUpdated?.(it, field, value); }}
                    readOnly={isCombinedView(activeView)}
                />
            ),
        },
        {
            key: 'rating',
            header: 'Rating',
            sortable: 'rating',
            width: '120px',
            renderCell: (item, { onItemUpdated } = {}) => (
                <InlineEditCell
                    item={item}
                    field="rating"
                    editType="rating"
                    displayValue={<StarRating rating={item.rating} />}
                    onSave={async (it, field, value) => { await handleSave(it, field, value); onItemUpdated?.(it, field, value); }}
                    readOnly={isCombinedView(activeView)}
                />
            ),
        },
        {
            key: 'date',
            header: 'Date',
            sortable: 'date',
            width: '110px',
            renderCell: (item) => (
                <span className="text-xs text-text-secondary">{formatDate(item.completedDate || item.lastPlayed)}</span>
            ),
        },
        ...(pod ? [{
            key: 'playedTogether',
            header: 'Played with',
            width: '110px',
            renderCell: (item) => {
                if (isCombinedView(activeView)) {
                    const coPartIds = item._coParticipantIds || [];
                    const coUsers = users.filter(u => coPartIds.includes(u.id));
                    return coUsers.length > 0
                        ? <span className="inline-flex items-center">{coUsers.map((u, i) => (
                            <span key={u.name} className={`text-sm ${i > 0 ? '-ml-1' : ''}`}>{u.emoji}</span>
                          ))}</span>
                        : <span className="text-text-tertiary">-</span>;
                }
                const coParticipants = (item._coParticipantIds || [])
                    .map(id => users.find(u => u.id === id))
                    .filter(Boolean);
                const otherMembers = (allMembers || []).filter(m => m.userId !== currentUser?.id);
                return (
                    <InlineActivityCell
                        item={item}
                        itemType="game"
                        activityType="played"
                        coParticipants={coParticipants}
                        otherMembers={otherMembers}
                        currentUser={currentUser}
                        pod={pod.id}
                        activityService={activityService}
                        onChanged={(newIds) => { item._coParticipantIds = newIds; }}
                    />
                );
            },
        }] : []),
        {
            key: 'playTime',
            header: 'Play Time',
            sortable: 'playTime',
            width: '100px',
            renderCell: (item, { onItemUpdated } = {}) => {
                const pt = formatPlayTime(item.playTime);
                return (
                    <InlineEditCell
                        item={item}
                        field="playTime"
                        editType="number"
                        displayValue={<span className="text-xs text-text-secondary">{pt ?? '—'}</span>}
                        onSave={async (it, field, value) => { await handleSave(it, field, value); onItemUpdated?.(it, field, value); }}
                        readOnly={isCombinedView(activeView)}
                    />
                );
            },
        },
        {
            key: 'progress',
            header: 'Progress',
            sortable: 'progress',
            width: '90px',
            renderCell: (item) => {
                const pct = item.progress;
                if (pct == null) return <span className="text-xs text-text-tertiary">—</span>;
                const color = pct === 100 ? 'text-emerald-400' : pct > 0 ? 'text-blue-400' : 'text-text-tertiary';
                return (
                    <div className="flex items-center gap-1.5">
                        <div className="flex-1 h-1.5 bg-bg-tertiary rounded-full overflow-hidden">
                            <div
                                className={`h-full rounded-full ${pct === 100 ? 'bg-emerald-400' : 'bg-blue-400'}`}
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                        <span className={`text-xs font-medium ${color}`}>{pct}%</span>
                    </div>
                );
            },
        },
        {
            key: 'source',
            header: 'Source',
            sortable: 'source',
            width: '80px',
            renderCell: (item) => {
                const src = item.source || 'steam';
                const label = src === 'xbox' ? 'Xbox' : 'Steam';
                const color = src === 'xbox' ? 'text-green-400' : 'text-blue-400';
                return <span className={`text-xs font-medium ${color}`}>{label}</span>;
            },
        },
    ];
}
